import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkles, Heart, Briefcase, Activity, Clock, DollarSign, Palette, Star, MessageCircle, Share2, Download, Facebook, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEME_ICONS = {
  love: Heart,
  career: Briefcase,
  health: Activity,
  longevity: Clock,
  finances: DollarSign,
  creativity: Palette,
  spirituality: Star,
};

const THEME_NAMES = {
  love: 'Amour',
  career: 'Carrière',
  health: 'Santé',
  longevity: 'Longévité',
  finances: 'Finances',
  creativity: 'Créativité',
  spirituality: 'Spiritualité',
};

const ResultsPage = () => {
  const { readingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [reading, setReading] = useState(location.state?.reading || null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [favorites, setFavorites] = useState({});
  const resultsRef = useRef(null);
  
  // Get session ID
  const sessionId = localStorage.getItem('lumina_session_id');

  useEffect(() => {
    if (!reading) {
      navigate('/scan');
    }
  }, [reading, navigate]);

  // Check which insights are already favorited
  useEffect(() => {
    const checkFavorites = async () => {
      if (!sessionId || !reading) return;
      
      try {
        const response = await axios.get(`${API}/favorites/${sessionId}`);
        const favMap = {};
        response.data.forEach(fav => {
          if (fav.reading_id === reading.id) {
            favMap[fav.theme] = fav.id;
          }
        });
        setFavorites(favMap);
      } catch (error) {
        console.error('Error checking favorites:', error);
      }
    };
    
    checkFavorites();
  }, [sessionId, reading]);

  const toggleFavorite = async (insight) => {
    if (!sessionId || !reading) return;
    
    if (favorites[insight.theme]) {
      // Remove from favorites
      try {
        await axios.delete(`${API}/favorites/${favorites[insight.theme]}?session_id=${sessionId}`);
        const newFavorites = { ...favorites };
        delete newFavorites[insight.theme];
        setFavorites(newFavorites);
        toast.success('Retiré des favoris');
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    } else {
      // Add to favorites
      try {
        const response = await axios.post(`${API}/favorites/add`, {
          session_id: sessionId,
          reading_id: reading.id,
          theme: insight.theme,
          title: insight.title,
          observation: insight.observation,
          interpretation: insight.interpretation,
          advice: insight.advice
        });
        setFavorites({ ...favorites, [insight.theme]: response.data.id });
        toast.success('Ajouté aux favoris');
      } catch (error) {
        toast.error('Erreur lors de l\'ajout');
      }
    }
  };

  if (!reading) return null;

  const { analysis_result } = reading;

  // Generate share text
  const getShareText = () => {
    const themes = reading.selected_themes?.map(t => THEME_NAMES[t] || t).join(', ');
    return `Je viens de découvrir ma lecture de main sur Lumina ! Thèmes explorés : ${themes}. Découvrez votre destinée aussi !`;
  };

  // Share functions
  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(window.location.origin);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
    setShowShareMenu(false);
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.origin);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodeURIComponent(getShareText())}`, '_blank');
    setShowShareMenu(false);
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(window.location.origin);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
    setShowShareMenu(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success('Lien copié !');
      setShowShareMenu(false);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Export PDF function
  const exportToPDF = async () => {
    setIsExporting(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // Helper function to add text with word wrap
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, index) => {
          if (y + (index * lineHeight) > pageHeight - margin) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, x, y + (index * lineHeight));
        });
        return y + (lines.length * lineHeight);
      };

      // Header
      pdf.setFillColor(139, 92, 246); // Purple
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('LUMINA', margin, 20);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Votre lecture de main personnalisée', margin, 30);

      yPosition = 55;

      // Date
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      const date = new Date(reading.created_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.text(`Lecture du ${date}`, margin, yPosition);
      yPosition += 10;

      // Themes analyzed
      pdf.setTextColor(60, 60, 60);
      pdf.setFontSize(11);
      const themesText = reading.selected_themes?.map(t => THEME_NAMES[t] || t).join(' • ') || '';
      pdf.text(`Thèmes : ${themesText}`, margin, yPosition);
      yPosition += 15;

      // General Impression
      if (analysis_result?.general_impression) {
        pdf.setFillColor(249, 250, 251);
        pdf.roundedRect(margin - 5, yPosition - 5, pageWidth - (margin * 2) + 10, 35, 3, 3, 'F');
        
        pdf.setTextColor(139, 92, 246);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Impression générale', margin, yPosition + 5);
        
        pdf.setTextColor(60, 60, 60);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        yPosition = addWrappedText(analysis_result.general_impression, margin, yPosition + 15, pageWidth - (margin * 2));
        yPosition += 15;
      }

      // Insights
      if (analysis_result?.insights) {
        for (const insight of analysis_result.insights) {
          // Check if we need a new page
          if (yPosition > pageHeight - 80) {
            pdf.addPage();
            yPosition = margin;
          }

          // Theme header
          pdf.setFillColor(245, 245, 245);
          pdf.roundedRect(margin - 5, yPosition - 3, pageWidth - (margin * 2) + 10, 12, 2, 2, 'F');
          
          pdf.setTextColor(139, 92, 246);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          const themeName = THEME_NAMES[insight.theme] || insight.theme;
          pdf.text(`${themeName} - ${insight.title}`, margin, yPosition + 5);
          yPosition += 18;

          // Observation
          pdf.setTextColor(80, 80, 80);
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Observation', margin, yPosition);
          yPosition += 5;
          
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(100, 100, 100);
          yPosition = addWrappedText(insight.observation || '', margin, yPosition, pageWidth - (margin * 2));
          yPosition += 8;

          // Interpretation
          pdf.setTextColor(80, 80, 80);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Interprétation', margin, yPosition);
          yPosition += 5;
          
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(100, 100, 100);
          yPosition = addWrappedText(insight.interpretation || '', margin, yPosition, pageWidth - (margin * 2));
          yPosition += 8;

          // Advice
          if (insight.advice) {
            pdf.setFillColor(254, 249, 195);
            const adviceLines = pdf.splitTextToSize(insight.advice, pageWidth - (margin * 2) - 10);
            const adviceHeight = adviceLines.length * 5 + 10;
            pdf.roundedRect(margin - 5, yPosition - 3, pageWidth - (margin * 2) + 10, adviceHeight, 2, 2, 'F');
            
            pdf.setTextColor(146, 64, 14);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.text('Conseil', margin, yPosition + 3);
            yPosition += 8;
            
            pdf.setFont('helvetica', 'normal');
            yPosition = addWrappedText(insight.advice, margin, yPosition, pageWidth - (margin * 2) - 10, 5);
            yPosition += 10;
          }

          yPosition += 5;
        }
      }

      // Footer
      pdf.setTextColor(150, 150, 150);
      pdf.setFontSize(8);
      pdf.text('Généré par Lumina - Lecture de main par IA', margin, pageHeight - 10);
      pdf.text(window.location.origin, pageWidth - margin - 50, pageHeight - 10);

      // Save PDF
      const fileName = `lumina-lecture-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exporté avec succès !');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-6xl mx-auto" ref={resultsRef}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <Button
              data-testid="back-to-home-button"
              variant="ghost"
              onClick={() => navigate('/')}
              className="rounded-full hover:bg-secondary/50"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à l'accueil
            </Button>
            
            {/* Share & Export Buttons */}
            <div className="flex gap-2">
              {/* Share Button */}
              <div className="relative">
                <Button
                  data-testid="share-button"
                  variant="outline"
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="rounded-full"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Partager
                </Button>
                
                {/* Share Menu Dropdown */}
                {showShareMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute right-0 top-full mt-2 bg-card border border-border rounded-xl shadow-lg p-2 z-50 min-w-[200px]"
                  >
                    <button
                      onClick={shareOnWhatsApp}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-green-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <span className="text-sm font-medium">WhatsApp</span>
                    </button>
                    
                    <button
                      onClick={shareOnFacebook}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <Facebook className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium">Facebook</span>
                    </button>
                    
                    <button
                      onClick={shareOnTwitter}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-sky-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center">
                        <Twitter className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm font-medium">Twitter</span>
                    </button>
                    
                    <hr className="my-2 border-border" />
                    
                    <button
                      onClick={copyLink}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-secondary/50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <Share2 className="h-4 w-4 text-gray-600" />
                      </div>
                      <span className="text-sm font-medium">Copier le lien</span>
                    </button>
                  </motion.div>
                )}
              </div>
              
              {/* Export PDF Button */}
              <Button
                data-testid="export-pdf-button"
                variant="outline"
                onClick={exportToPDF}
                disabled={isExporting}
                className="rounded-full"
              >
                <Download className="h-4 w-4 mr-2" />
                {isExporting ? 'Export...' : 'Export PDF'}
              </Button>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-3">
                Votre lecture de main
              </h1>
              <p className="text-muted-foreground text-base md:text-lg">
                Découvrez les secrets de votre destinée
              </p>
            </div>
          </div>
        </motion.div>

        {/* General Impression */}
        {analysis_result?.general_impression && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-primary to-primary/80 p-8 md:p-10 rounded-3xl shadow-xl mb-8 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32" />
            <div className="relative z-10">
              <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
                Impression générale
              </h2>
              <p className="text-white/95 text-base md:text-lg leading-relaxed">
                {analysis_result.general_impression}
              </p>
            </div>
          </motion.div>
        )}

        {/* Insights Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {analysis_result?.insights?.map((insight, index) => {
            const Icon = THEME_ICONS[insight.theme] || Sparkles;
            const isHero = index === 0;

            return (
              <motion.div
                key={index}
                data-testid={`insight-card-${insight.theme}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className={`bg-card p-6 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all duration-300 ${
                  isHero ? 'md:col-span-2' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl mb-1">{insight.title}</h3>
                      <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                        {THEME_NAMES[insight.theme] || insight.theme}
                      </span>
                    </div>
                  </div>
                  
                  {/* Favorite Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(insight);
                    }}
                    className={`p-2 rounded-full transition-all ${
                      favorites[insight.theme]
                        ? 'bg-yellow-100 text-yellow-600'
                        : 'bg-secondary/30 text-muted-foreground hover:bg-yellow-50 hover:text-yellow-500'
                    }`}
                    title={favorites[insight.theme] ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                  >
                    <Star className={`h-5 w-5 ${favorites[insight.theme] ? 'fill-yellow-500' : ''}`} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Observation</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {insight.observation}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">Interprétation</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {insight.interpretation}
                    </p>
                  </div>

                  {insight.advice && (
                    <div className="bg-secondary/30 p-4 rounded-xl">
                      <h4 className="text-sm font-semibold text-secondary-foreground mb-2">
                        Conseil
                      </h4>
                      <p className="text-sm text-secondary-foreground/80 leading-relaxed">
                        {insight.advice}
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 flex flex-wrap gap-4 justify-center"
        >
          <Button
            data-testid="chat-button"
            onClick={() => navigate('/chat', { state: { reading } })}
            size="lg"
            className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Discuter avec l'IA
          </Button>
          <Button
            data-testid="new-reading-button"
            onClick={() => navigate('/scan')}
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Sparkles className="mr-2 h-5 w-5" />
            Nouvelle lecture
          </Button>
          <Button
            data-testid="view-all-readings-button"
            onClick={() => navigate('/history')}
            variant="outline"
            size="lg"
            className="rounded-full border-2 px-8 py-6 text-base font-semibold hover:bg-secondary/50 transition-all duration-300"
          >
            Voir mon historique
          </Button>
        </motion.div>
      </div>
      
      {/* Click outside to close share menu */}
      {showShareMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowShareMenu(false)}
        />
      )}
    </div>
  );
};

export default ResultsPage;
